/**
 * Token status markers, like a VTT's status icons: an icon and a ring colour for each condition
 * or effect, most urgent first.
 */
import type { CreatureView } from '../shared/view.ts';

export interface StatusStyle {
  icon: string;
  color: string;
  order: number;
}

/** Persistent damage, by its type: it burns, bleeds or eats at the bearer every turn. */
const PERSISTENT: [string, string, string][] = ([
  ['bleed', '🩸', '#d04040'], ['fire', '🔥', '#ff8a3d'], ['acid', '🧪', '#7cc444'], ['cold', '❄️', '#7fd3ff'], ['electricity', '⚡', '#7fd3ff'],
  ['poison', '☠️', '#7cc444'], ['sonic', '🔊', '#b36bff'], ['force', '✴️', '#c49bff'], ['vitality', '☀️', '#ffe9a0'], ['void', '🕳️', '#9b6bc8'],
  ['mental', '🧠', '#c49bff'], ['spirit', '👻', '#c8d4e0'],
] as [string, string, string][]).map(([t, icon, color]) => [`persistent-${t}`, icon, color]);

const ORDER = [
  // Conditions: the ones that change what a creature can do come first.
  ['dying', '💀', '#e04848'],
  // An avenger's Zealous Inevitability: death closes in.
  ['doomed', '💀', '#9b6bc8'],
  ['unconscious', '💤', '#9aa0b8'],
  ['groggy', '😵', '#c9a36b'],
  ['paralyzed', '🧊', '#7fd3ff'],
  ['asleep', '😴', '#9aa0b8'],
  ['frightened', '😱', '#b36bff'],
  // Terrified Retreat's flight, and Bon Mot's distraction.
  ['fleeing', '🏃', '#e0b84a'],
  ['bon-mot', '💬', '#c49bff'],
  ['prone', '🛌', '#c9a36b'],
  ['kept-down', '⏬', '#d06060'],
  ['off-guard', '🎯', '#f0a030'],
  ['stunned', '💫', '#ffd24a'],
  ['restrained', '🪢', '#a7b0b8'],
  ['grabbed', '🤼', '#a7b0b8'],
  ['pinned', '📌', '#a7b0b8'],
  ['immobilized', '⛓️', '#a7b0b8'],
  ['slowed', '🐌', '#c9a36b'],
  ['blinded', '🙈', '#9a9a9a'],
  ['dazzled', '✨', '#ffe066'],
  ['sickened', '🤢', '#7cc444'],
  ['fatigued', '🥱', '#c9a36b'],
  ['enfeebled', '🥀', '#d06060'],
  ['clumsy', '🥴', '#d99a70'],
  ['stupefied', '🫠', '#c49bff'],
  ['fascinated', '🤩', '#ffd24a'],
  ['deafened', '🙉', '#9a9a9a'],
  ['charmed', '💕', '#ff9ccf'],
  ['invisible', '🌫️', '#c8d4e0'],
  ...PERSISTENT,
  // Stealth: your Stealth has foes losing you (with how many), a foe that has lost you (in your view only),
  // the exploration activities (while exploring), and a Feint's opening.
  ['undetected', '🥷', '#8fa3b8'],
  ['hidden', '👤', '#a7b8c8'],
  ['unaware-of-you', '🫥', '#7fc4a4'],
  ['hears-you', '👂', '#e0b84a'],
  ['avoiding-notice', '🤫', '#9aa0b8'],
  ['following-the-expert', '👣', '#9aa0b8'],
  ['scouting', '🔭', '#9aa0b8'],
  ['searching', '🔎', '#9aa0b8'],
  ['anticipating-ambush', '👁️', '#9aa0b8'],
  ['feinted', '🎭', '#f0a030'],
  ['hidden-paragon', '🌑', '#8fa3b8'],
  ['wounded', '🩸', '#b02a2a'],
  ['prey', '🏹', '#e05a4a'],
  ['opening', '💥', '#f0a030'],
  ['exposed', '💥', '#f0a030'],
  // Gunslinger feats: a foe ducking under Cover Fire, one opened up by Sword and Pistol, a pistolero's duel, and a foe Called Shot hobbled.
  ['ducked', '🪖', '#c9a36b'],
  ['sword-and-pistol-opening', '⚔️', '#f0a030'],
  ['challenged', '🤠', '#e05a4a'],
  ['pistoleros-challenge', '🤠', '#e8c15a'],
  ['hobbled', '🦵', '#d06060'],
  ['assisted', '🎯', '#7fd3ff'],
  // The commander: a foe's opened guard (Set-up Strike), the confused, a turned mercenary, the banner's gifts.
  ['set-up-opening', '🎯', '#e0a050'],
  ['confused', '😵', '#c49bff'],
  ['mercenary-reversal', '🤝', '#7fcf9f'],
  ['banner-planted', '🚩', '#c0504a'],
  ['banner-twirl', '🌀', '#c0504a'],
  ['defiant-banner', '🛡️', '#c0504a'],
  ['quickening-banner', '⏩', '#c0504a'],
  ['pennant-of-victory', '🏆', '#e8c15a'],
  ['lodged-weapon', '🗡️', '#e0a050'],
  // Specialty ammunition: the light a round left on the creature it struck, and the marks rounds leave.
  ['limned', '✴️', '#ffe08a'],
  ['shining-round', '💡', '#fff3a6'],
  ['daybreak-flare', '🌅', '#ffcf6e'],
  ['beacon-sparks', '🎇', '#ffd27a'],
  ['silenced', '🔇', '#9aa7b8'],
  ['reeking', '👃', '#b5c46a'],
  ['exsanguinated', '🩸', '#d06060'],
  ['locked-on', '🔒', '#e05a4a'],
  ['tampered', '🔧', '#f0a030'],
  ['quickened', '⏩', '#5ccf5c'],
  ['unluck', '🎲', '#b36bff'],
  ['shield-raised', '🛡️', '#9cc8ff'],
  // Firearms and crossbows: a jammed weapon, one that must be reloaded before its next shot, and a shooter hunkered down prone.
  ['jammed', '🔩', '#d06060'],
  ['unloaded', '⏳', '#c9a36b'],
  ['taking-cover', '🧱', '#9cc8ff'],
  // The gunslinger's deeds: a vanguard's living fortification, a triggerbrand's sprung trap and its wind-up, and a foe a triggerbrand wound up.
  ['living-fortification', '🏰', '#9cc8ff'],
  ['spring-the-trap', '🪤', '#c9a36b'],
  ['wound-them-up', '🎭', '#c9a36b'],
  ['wound-up', '🪙', '#e0b84a'],
  // The rogue: Mastermind's mark, Debilitating Strike's debilitations, and what the rogue feats leave on foes.
  ['outwitted', '🧠', '#c9a0ff'],
  ['identified', '🔍', '#9fc5ff'],
  ['debilitated-speed', '🦶', '#d06060'],
  ['debilitated-weak-bludgeoning', '💢', '#d06060'],
  ['debilitated-weak-piercing', '💢', '#d06060'],
  ['debilitated-weak-slashing', '💢', '#d06060'],
  ['debilitated-precise', '🎯', '#d06060'],
  ['debilitated-no-reactions', '🚫', '#d06060'],
  ['debilitated-no-flanking', '↔️', '#d06060'],
  ['debilitated-no-shield', '🛡️', '#d06060'],
  ['debilitated-no-step', '⛔', '#d06060'],
  ['overextended', '🎣', '#f0a030'],
  ['overextended-once', '🎣', '#f0a030'],
  ['distracted', '💫', '#f0a030'],
  ['unbalanced', '🌀', '#d06060'],
  ['tumbled-past', '🤸', '#d06060'],
  ['predicted', '🔮', '#9fc5ff'],
  ['misread', '🔮', '#9fc5ff'],
  ['analyzed-weakness', '🔬', '#d06060'],
  ['sly-disarmed', '🫳', '#d06060'],
  ['watch-your-back', '👀', '#e0b84a'],
  ['powerful-sneak', '🗡️', '#f0a030'],
  ['bullseye', '◎', '#7fc4a4'],
  ['cognitive-loophole', '🧩', '#9fc5ff'],
  // Spells and other short effects.
  ['entangled', '🕸️', '#b0b0b0'],
  ['weakened-grasp', '✋', '#d06060'],
  ['haste', '⚡', '#ffd24a'],
  ['accelerated', '👟', '#5ccf5c'],
  ['heroism', '🦸', '#ffd24a'],
  ['blessed', '🙏', '#ffe9a0'],
  ['protected', '🔰', '#9cc8ff'],
  ['shield-spell', '🔷', '#7fb4ff'],
  ['shield-of-faith', '🔆', '#ffe9a0'],
  ['divine-aura', '😇', '#ffe9a0'],
  ['raised-symbol', '📿', '#ffe9a0'],
  ['dueling-parry', '🤺', '#9cc8ff'],
  ['twin-parry', '⚔️', '#9cc8ff'],
  ['resounding-bravery', '🦁', '#ffd24a'],
  // Fighter stances.
  ['point-blank-stance', '🏹', '#9cc8ff'],
  ['disarming-stance', '🤺', '#9cc8ff'],
  ['ricochet-stance', '🪃', '#9cc8ff'],
  ['mobile-shot-stance', '🏃', '#9cc8ff'],
  ['disruptive-stance', '⛔', '#9cc8ff'],
  ['dueling-dance', '💃', '#9cc8ff'],
  ['lunging-stance', '➶', '#9cc8ff'],
  ['paragons-guard', '🛡️', '#9cc8ff'],
  ['graceful-poise', '🦂', '#9cc8ff'],
  ['multishot-stance', '🎯', '#9cc8ff'],
  ['twinned-defense', '⚔️', '#9cc8ff'],
  ['haft-striker-stance', '🔨', '#9cc8ff'],
  ['blurred', '👥', '#b8c4ff'],
  ['fire-shield', '🔥', '#ff8a3d'],
  ['barkskin', '🌳', '#7cc444'],
  ['mountain-resilience', '⛰️', '#a7b0b8'],
  ['resist-energy', '🌀', '#7fd3ff'],
  ['regenerating', '💚', '#5ccf5c'],
  ['field-of-life', '🌿', '#5ccf5c'],
  // The wizard's ward and form, and divine and primal blessings and banes.
  ['protective-wards', '🧿', '#7fb4ff'],
  ['shifting-form', '🐾', '#c9a36b'],
  ['bespelled', '🪄', '#c49bff'],
  ['divine-weapon', '🌟', '#ffe9a0'],
  ['sanctified-armament', '🔱', '#ffe9a0'],
  ['stolen-spell', '🎇', '#c49bff'],
  ['defensive-recovery', '🛡️', '#ffe9a0'],
  ['deitys-protection', '🙌', '#ffe9a0'],
  ['eternal-blessing', '🌞', '#ffe9a0'],
  ['eternal-bane', '🌘', '#b36bff'],
  ['energy-ablation', '🌈', '#7fd3ff'],
  ['wandering-oasis', '🏝️', '#7fd3ff'],
  ['primal-aegis', '🍃', '#7cc444'],
  ['current-spell', '🌊', '#7fb4ff'],
  ['uplifting-winds', '🌬️', '#b8e4ff'],
  ['rejuvenating-touch', '🌱', '#5ccf5c'],
  ['swift-retribution', '⏩', '#ffd24a'],
  ['guardians-cover', '🛡️', '#9cc8ff'],
  ['enchant-hindered', '🪨', '#d06060'],
  ['bomb-speed-penalty', '🦶', '#d06060'],
  // Inventor gadgets.
  ['critical-overdrive', '⚙️', '#ff7a2f'],
  ['overdrive', '⚙️', '#ffb347'],
  ['overdrive-ally', '⚙️', '#ffd27f'],
  ['electrified-armor', '⚡', '#7fd3ff'],
  ['geobukseon-shell', '🐢', '#c9a36b'],
  ['innovation-unstable', '💨', '#9a9a9a'],
  ['innovation-heat', '🌡️', '#ff7a2f'],
  ['overdrive-sputter', '⚙️', '#9a9a9a'],
  ['flaws-found', '🪛', '#d06060'],
  ['flaws-misjudged', '❓', '#9a9a9a'],
  ['plug-held', '🌪️', '#c8d4e0'],
  ['link-biotic-bane', '💔', '#d06060'],
  // Inventors+: the Unconventions' overdrives and what their gizmos leave behind.
  ['critical-reverberate', '🔔', '#ff7a2f'],
  ['reverberate', '🔔', '#ffb347'],
  ['critical-frigerate', '❄️', '#7fd3ff'],
  ['frigerate', '❄️', '#b8e4ff'],
  ['critical-crank-up', '🏭', '#ff7a2f'],
  ['crank-up', '🏭', '#ffb347'],
  ['critical-mutate', '🧬', '#7cc444'],
  ['mutate', '🧬', '#a6dc7a'],
  ['critical-repulse', '🧲', '#ff7a2f'],
  ['repulse', '🧲', '#ffb347'],
  ['reverberating', '🔔', '#b36bff'],
  ['frigerated', '🥶', '#7fd3ff'],
  // Inventors+: healing links, magitek, armor and construct modes, weapons.
  ['invigoration-link', '💉', '#7fc8a9'],
  ['link-critical-enhancement', '💪', '#7fc8a9'],
  ['overheal', '💗', '#7fc8a9'],
  ['planar-focus', '🌌', '#c49bff'],
  ['planar-focus-undead', '🌌', '#c49bff'],
  ['concealing-holograms', '🎭', '#9b8cff'],
  ['unavoidable-ray', '🎯', '#9b8cff'],
  ['fail-safe-operator', '🤖', '#ff7a2f'],
  ['metaphysical-materials', '👻', '#c8d4e0'],
  ['swarm-state', '🐜', '#c9a24a'],
  ['parrying', '🤺', '#9cc8ff'],
  ['conducted-acid', '🧪', '#7cc444'],
  ['conducted-cold', '❄️', '#7fd3ff'],
  ['conducted-electricity', '⚡', '#7fd3ff'],
  ['conducted-fire', '🔥', '#ff8a3d'],
  ['conducted-sonic', '🔊', '#b36bff'],
  // Inventors+ gadgets at work.
  ['phantasm-plug', '🌀', '#c8d4e0'],
  ['miniaturized', '🤏', '#c9a36b'],
  ['miniaturized-2', '🤏', '#c9a36b'],
  ['miniaturized-3', '🤏', '#c9a36b'],
  ['jetpack', '🚀', '#ffb347'],
  ['power-gauntlet', '🥊', '#ff8a3d'],
  ['hook-chain', '🪝', '#a7b0b8'],
  // The magus.
  ['spellstrike-discharged', '🔋', '#9a9a9a'],
  ['meteor-trail', '☄️', '#ffd27f'],
  ['spell-parry', '🤺', '#c49bff'],
  ['conjurers-countermeasure', '🛡️', '#c49bff'],
  ['hasted-assault', '⚡', '#ffd24a'],
  ['warding-aggression', '🔰', '#c49bff'],
  ['flickering', '✨', '#b8c4ff'],
  ['enlarged', '🦣', '#c9a36b'],
  ['enlarged-huge', '🦣', '#c9a36b'],
  ['flying', '🕊️', '#b8c4ff'],
  ['gecko-grip', '🦎', '#7cc444'],
  ['ghostly-weapon', '👻', '#c8d4e0'],
  ['arcane-shroud', '⛰️', '#a7b0b8'],
  // Rogue feats waiting to be ready again.
  ['decoy-spent', '⌛', '#9a9a9a'],
  ['defensive-roll-used', '⌛', '#9a9a9a'],
  ['hidden-paragon-used', '⌛', '#9a9a9a'],
  ['haft-beatdown-used', '⌛', '#9a9a9a'],
  ['prescient-planner-spent', '⌛', '#9a9a9a'],
] as const;

/** Arcane Cascade is one stance per damage type (and Resounding Cascade one aura per type): the same icon for each. */
const CASCADE_DAMAGE_TYPES = ['force', 'acid', 'cold', 'electricity', 'fire', 'sonic', 'mental', 'spirit', 'vitality', 'void', 'poison', 'bludgeoning', 'piercing', 'slashing'];
const CASCADES: [string, string, string][] = CASCADE_DAMAGE_TYPES.flatMap((t) => [
  [`arcane-cascade-${t}`, '🌀', '#c49bff'] as [string, string, string],
  [`resounding-cascade-${t}`, '🌀', '#d9c6ff'] as [string, string, string],
]);
const RUNES = ['corrosive', 'flaming', 'frost', 'shock', 'thundering', 'ghost-touch', 'returning', 'keen'].flatMap((r): [string, string, string][] => [
  [`runic-impression-${r}`, '🔆', '#c49bff'],
  // An inventor's magitek Runic Upgrade scribes the same runes.
  [`runic-upgrade-${r}`, '🔆', '#9b8cff'],
]);
/** An inventor's theories: one marker for all of them. */
const THEORIES = ['condition-causation', 'spontaneous-ingenuity', 'elemental-entanglement', 'of-everything'].map((t): [string, string, string] => [`theory-${t}`, '💡', '#ffd24a']);
/** A weakness opened up (Forcible Energy, an inventor's Elemental Entanglement), and an inventor's tinkered weapon: one marker each, per type. */
const PER_TYPE: [string, string, string][] = ['acid', 'cold', 'electricity', 'fire', 'poison', 'sonic'].flatMap((t): [string, string, string][] => [
  [`forcible-energy-${t}`, '💢', '#d06060'],
  [`entangled-weakness-${t}`, '💢', '#d06060'],
]).concat(['cold', 'fire', 'electricity', 'bludgeoning', 'slashing', 'piercing', 'acid'].map((t): [string, string, string] => [`tinkered-${t}`, '🛠️', '#9b8cff']));

const ALL = [...ORDER, ...CASCADES, ...RUNES, ...THEORIES, ...PER_TYPE];

export const STATUS_STYLE: Record<string, StatusStyle> = Object.fromEntries(ALL.map(([id, icon, color], order) => [id, { icon, color, order }]));

export function statusStyle(s: { id: string; name: string }): StatusStyle {
  return STATUS_STYLE[s.id] ?? { icon: s.name.slice(0, 1).toUpperCase(), color: '#a7b0b8', order: ALL.length };
}

/** A creature's markers, most urgent first. */
export function sortedStatus(c: CreatureView): CreatureView['status'] {
  return [...c.status].sort((a, b) => statusStyle(a).order - statusStyle(b).order);
}

/** "😱 frightened 2 · 🛌 prone" for tooltips. */
export function statusLine(c: CreatureView): string {
  return sortedStatus(c).map((s) => `${statusStyle(s).icon} ${s.name.toLowerCase()}${s.value !== undefined ? ` ${s.value}` : ''}`).join(' · ');
}

/** A token's mark colour for its faction's hue (the same on the map and in its tooltip). */
export function factionColor(hue: number): string {
  return `hsl(${hue} 70% 60%)`;
}

export const EMOJI_FONT = '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", ui-sans-serif, system-ui, sans-serif';
